// Lanzar muchas peticiones a la vez, pero no TODAS a la vez.
//
// ── EL PROBLEMA ────────────────────────────────────────────────────────────
//
// Al entrar, Deskte se trae el libro entero de clientes y el de polizas. Pide
// la pagina 1, mira cuantas hay, y lanza TODAS las que faltan de golpe con un
// `Promise.all`. Eso se hizo a proposito y arreglo algo real: cargarlas de una
// en una hacia que recargar tardara una eternidad (Lissette 2026-06-30 "el
// reload sigue lento"; 2026-08-07 "carga lento clientes y polizas").
//
// Pero con 14.500 polizas son 30 paginas, y cada pagina es una consulta pesada.
// Salen las 30 juntas — y las de clientes al mismo tiempo. Cuando entran varias
// agencias a la vez, el servidor recibe cientos de consultas pesadas en el
// mismo segundo y todas se ponen en fila. La pantalla no va mas rapida por
// pedirlo todo de golpe: va mas lenta, porque la fila la hace el servidor.
//
// ── LO QUE CAMBIA ──────────────────────────────────────────────────────────
//
// No se vuelve a la carga de una en una: eso ya se probo y era peor. Se pone un
// TECHO de cuantas van en vuelo a la vez. Con 4, treinta paginas salen en ocho
// tandas en vez de una — sigue siendo cuatro veces mas rapido que en serie, y
// el servidor no recibe una avalancha.
//
// El 4 es una perilla, no una verdad: si algun dia se mide que el servidor
// aguanta mas, se sube aqui y lo heredan todos los sitios que lo usan.

export const ANCHO_POR_DEFECTO = 4;

/**
 * El techo para el boton "Actualizar" de Hojas, que se trae los DOS libros
 * enteros (clientes y polizas) a demanda.
 *
 * Es 3 y no 4 a proposito. El arranque pasa UNA vez por sesion; este boton se
 * aprieta a media jornada, con el resto del equipo trabajando. Medido en
 * produccion el 2026-09-03 a las 21:30, cuando todavia salian todas de golpe:
 * arranco `policies?page=1&limit=500` y TODO lo demas -actividad, licencias,
 * alertas, plantillas, presencia- termino de golpe **9 segundos despues**, en
 * el mismo instante. Eso no es lentitud: es una cola. Y los 9 segundos los
 * pagaron los agentes que no apretaron nada.
 *
 * El otro motivo del 3: las paginas de los dos libros tienen que ir por el
 * MISMO limitador. Con uno por libro serian 3 + 3 = 6 encima del servidor, que
 * es justo lo que esto viene a evitar.
 */
export const ANCHO_DE_HOJAS = 3;

/**
 * Ejecuta las tareas con como mucho `ancho` en vuelo, y devuelve los
 * resultados EN EL MISMO ORDEN en que se pasaron.
 *
 * El orden importa: quien llama pega las paginas una detras de otra, y si
 * llegaran en el orden en que terminan, el listado saldria barajado.
 *
 * @param {Array<() => Promise<any>>} tareas funciones que devuelven promesa.
 *   Se pasan SIN llamar: llamarlas antes seria lanzarlas todas a la vez, que
 *   es justo lo que esto viene a evitar.
 * @param {number} ancho cuantas como mucho a la vez.
 */
export async function enTandas(tareas, ancho = ANCHO_POR_DEFECTO) {
  const lista = Array.isArray(tareas) ? tareas : [];
  if (lista.length === 0) return [];
  // Un ancho invalido (0, negativo, NaN) no puede dejar el bucle sin avanzar:
  // se lee como "de una en una", que es lento pero termina.
  const tope = Math.max(1, Math.min(Math.floor(Number(ancho) || 1), lista.length));

  const resultados = new Array(lista.length);
  let siguiente = 0;

  async function obrero() {
    for (;;) {
      const i = siguiente;
      siguiente += 1;
      if (i >= lista.length) return;
      const tarea = lista[i];
      resultados[i] = typeof tarea === 'function' ? await tarea() : undefined;
    }
  }

  await Promise.all(Array.from({ length: tope }, () => obrero()));
  return resultados;
}
